/**
 * The pull-side resume catch-up.
 *
 * `DatabaseWrapper` holds two different things at once: a replication engine
 * that keeps this device's local PouchDB in step with the remote CouchDB, and a
 * document-access API (get / save / query) that the rest of the app calls. This
 * file holds one piece of the first one — the recovery path that notices the
 * live download connection has silently died, fetches what this device missed
 * straight from the server, grafts it in, and restarts the live sync.
 *
 * Like `database-wrapper-sync-start.ts`, it deliberately knows nothing about
 * `DatabaseWrapper`. Everything it touches arrives through the
 * `PullCatchupContext` object below, which is why the module can be exercised in
 * a unit test with a small fake instead of a whole database wrapper — the class
 * itself imports `pouchdb-browser` and cannot even be loaded outside a browser,
 * so before this move the only way to protect the rules below was to read this
 * function's own source text and pattern-match on it.
 *
 * Nothing here changed behaviour when it moved out of `database-wrapper.ts`.
 * The invariants below were already true and are restated because they are easy
 * to break with an edit that looks harmless:
 *
 *  - **This function must never push to the remote** (#3988 F17). On resume the
 *    recovery triad fires `verifyPushCheckpointIntegrityAsync()`,
 *    `ensureSyncsActive()` and then this, fire-and-forget, so they overlap. A
 *    push here would race the checkpoint check into 409 conflicts (the Sentry
 *    3ZD class). The local `bulkDocs(new_edits:false)` graft is a local write,
 *    not a push, and is allowed. See `design-docs/sync-integrity.md`.
 *  - **It skips entirely during coach/admin impersonation** (#3987 A6).
 *    Impersonation routes reads to `temporaryDb`, so a catch-up would compare
 *    the admin's own remote against a local database the session is not using
 *    and graft or report false misses.
 *  - **The cooldown decision is made BEFORE the recreation gate is taken**, so
 *    the common case — a resume that is still inside the cooldown — costs
 *    nothing.
 *  - **The read lock is released in a `finally`**, and the pulled documents are
 *    handed to the listeners in a `finally` of their own: if one apply throws
 *    part-way through, everything collected so far must still reach the
 *    listeners or that whole batch of cross-device changes is lost with nothing
 *    to re-trigger it.
 *  - **Pulled documents reach the listeners once per batch, never per document.**
 *    A graft can carry thousands. See `design-docs/reactive-sync.md`.
 */

import { DateTime } from 'luxon';
import { PouchDoc } from '@cf-shared/data-model/pouch';
import { isTransientFetchError } from './error-patterns';
import type { ClosedHandleOrigin } from './database-recovery.service';
import {
  DocIdKeyRange,
} from './initial-sync-progress';
import {
  docTypeFromId,
} from './sync-telemetry-shaping';
import {
  decidePullCatchup,
  isSyncDiagnosticEnvironmentHealthy,
  PullCatchupDecision,
  PullCatchupLocalRow,
  PullCatchupStats,
  selectMissedDocs,
} from './sync-health-decisions';

/**
 * Everything the pull catch-up needs from the database wrapper around it.
 *
 * Grouped on purpose, and a new member goes in the group it belongs to: state
 * this module only reads, constants that live on the wrapper, and things it asks
 * the wrapper to do. A member here is not an invitation to add more — each one
 * is a piece of the document-access side that the sync engine has not been
 * separated from yet. See deferred finding `df-3c79746add23`.
 */
export interface PullCatchupContext {
  // ---- state this module only reads ----

  /** set while a coach or admin is impersonating another account */
  readonly temporaryDb: any;
  readonly remoteDb: any;
  readonly localDb: any;
  /** the name used in log lines and telemetry, e.g. `user` */
  readonly internalDatabaseIdentifier: string;
  readonly initialSyncCompleted: boolean | undefined;
  readonly syncHandler: any;
  readonly syncHandlerIsInitial: boolean;
  /**
   * The connection state, in the two shapes this module consumes it. The
   * boolean keeps this module from having to import the enum, which lives in a
   * module that reaches the Capacitor and environment layer and cannot load
   * outside a browser.
   */
  readonly connectionStateIsOnline: boolean;
  /** the connection state's own value, published verbatim in the Sentry context */
  readonly connectionState: string;
  readonly logging: {
    trackSilentInfo(name: string, context: { [key: string]: any }): void;
    trackSilentProblem(name: string, context: { [key: string]: any }): void;
  };
  readonly databaseRecovery: {
    /** true while a purge or a restore is destroying this database on purpose */
    isIntentionalDestruction(): boolean;
    /**
     * Offers a failure to the database recovery. True means a recovery has taken it — the
     * local store had gone bad and is being repaired or the page replaced — so this path
     * must not report it as its own fault (#6230).
     */
    interceptDatabaseError(err: unknown, origin?: ClosedHandleOrigin): boolean;
  };
  readonly telemetry: { trackEvent(name: string, params: { [key: string]: any }): void } | null;

  // ---- constants that live on the wrapper ----

  /** how long one device waits between catch-up runs */
  readonly pullCatchupCooldownMs: number;
  /** the id ranges the priority window covers, shared with the conflict canary */
  getPrioritySyncKeyRanges(): DocIdKeyRange[];

  // ---- things it asks the wrapper to do ----

  /** the catch-up runs for the user database only */
  isUserDb(): boolean;
  /** waits out a concurrent local-database recreation; false means give up */
  waitForRecreationGateAsync(): Promise<boolean>;
  /** false means another writer holds the lock and this run must not start */
  acquireReadLock(): boolean;
  releaseReadLock(): void;
  replicationFilter(doc: PouchDoc): boolean;
  /** applies one pulled doc to the identity map, collecting it for the batch */
  collectAppliedPulledDoc(doc: any, listenerDocsOut: any[]): void;
  deliverPulledDocsToListeners(docs: any[]): void;
  hasRecentNetworkSyncError(): boolean;
  /** the last sync error as a string, so Sentry does not flatten it to "[Object]" */
  stringifiedLastSyncError(): string | null;
  cancelSync(): void;
  startSync(initial: boolean): Promise<void> | void;
}

/**
 * #6230: a remote read failed because the server itself was down.
 *
 * Thrown only around the REMOTE reads, never for a local one. That matters: PouchDB's own
 * local-store errors also carry status 500 (`indexed_db_went_bad` is `PouchError(500, …)`), so
 * a bare "status is 5xx" test in the outer catch would take a broken local store for a server
 * outage and never hand it to the database recovery.
 *
 * Two shapes reach it. A 5xx with a JSON body arrives from PouchDB with its status. A 5xx whose
 * body is not JSON (a load balancer's HTML error page) arrives as a bare `SyntaxError` with no
 * status at all, because PouchDB parses the body before it looks at the status; the catch-up
 * then asks the server for its status, and `status` is null when even that answer was not JSON.
 */
export class PullCatchupServerUnavailableError extends Error {
  constructor(readonly status: number | null, cause: unknown) {
    super(status === null ? 'the server is not answering with JSON' : `the server answered ${status}`, { cause });
    this.name = 'PullCatchupServerUnavailableError';
  }
}

/** the lowest HTTP status that means "the server failed", not "the request was wrong" */
const kFirstServerErrorStatus = 500;

/** the status probe's answer when the server's reply to it was not JSON either */
const kProbeAnswerNotJson = 'not-json';

/** does this remote-read error say the server itself failed? */
function isServerErrorStatus(err: any): boolean {
  return typeof err?.status === 'number' && err.status >= kFirstServerErrorStatus;
}

/**
 * #6230: why a failed catch-up is NOT this path's fault, or `null` when it is and must be
 * reported. Each answer names the part of the app that already owns that failure.
 *
 * Not a pure lookup: a local-store error is HANDED to the database recovery here, which can
 * start a repair. Call it once per failure.
 */
function handOffFailureOwnedElsewhere(ctx: PullCatchupContext, err: any): string | null {
  // if the server was down, the next resume or reconnect runs the catch-up again
  if (err instanceof PullCatchupServerUnavailableError) {
    return `server unavailable (${err.status ?? 'no JSON answer'})`;
  }
  // if the sign-in is still being redone after a resume, the live sync owns repeated auth
  // failures (handleSyncAuthError escalates after 3 in a row). only a 401: a 403 can mean
  // access was revoked, and stays reported.
  if (err?.status === 401) {
    return 'not signed in to the database yet — the live sync owns repeated auth failures';
  }
  // if the local store has gone bad, the database recovery owns it — this hands it over,
  // which starts the repair when nothing else has yet. a local error the recovery does not
  // take (no family matches it) falls through and is reported.
  if (ctx.databaseRecovery.interceptDatabaseError(err, 'pull-catchup')) {
    return 'handed to the database recovery';
  }
  return null;
}

/**
 * Run one pull-side catch-up for this database.
 *
 * The caller (`DatabaseWrapper.ensurePullCatchupAsync`) only builds the context;
 * every gate below — impersonation, database kind, the cooldown decision, the
 * recreation gate and the read lock — is decided here.
 */
export async function ensurePullCatchupInternalAsync(ctx: PullCatchupContext): Promise<PullCatchupStats> {
  // #3987 A6: coach/admin impersonation routes reads to temporaryDb; a catch-up here
  // would compare the admin's own remote against a local DB the session isn't using
  // and graft/report false misses (leading hypothesis for APP-46M's repeated 94-doc
  // "recovery" by an admin account). startSyncInternal has the same guard.
  if (ctx.temporaryDb) return { ran: false, skipReason: 'impersonation' };
  if (!ctx.isUserDb() || !ctx.localDb || !ctx.remoteDb) return { ran: false, skipReason: 'not-applicable' };

  // decide before taking the recreation gate — most resume/online calls skip on cooldown
  const nowMs = DateTime.utc().toMillis();
  const lastCatchupRaw = sessionStorage.lastPullCatchup_utc;
  const lastCatchupMs = lastCatchupRaw ? DateTime.fromISO(lastCatchupRaw).toMillis() : null;
  const decision = decidePullCatchup({
    initialSyncCompleted: ctx.initialSyncCompleted,
    hasLiveSyncHandler: ctx.syncHandler != null,
    syncHandlerIsInitial: ctx.syncHandlerIsInitial,
    connectionStateIsOnline: ctx.connectionStateIsOnline,
    lastCatchupMs,
    nowMs,
    cooldownMs: ctx.pullCatchupCooldownMs,
  });
  if (decision !== PullCatchupDecision.Run) {
    // a cooldown skip is the common, expected case — keep it quiet
    if (decision !== PullCatchupDecision.SkipCooldown) {
      console.log(`Db:${ctx.internalDatabaseIdentifier}: pull catch-up skipped (${decision})`);
    }
    return { ran: false, skipReason: decision };
  }

  // O5 (#4027 Step 25): time the run for the soft-resume `resume_recovery` event
  const runStartMs = performance.now();

  // guard the multi-await graft against a concurrent localDb recreation (sign-out, coach
  // swap, recovery) — same protection the sibling direct-pull syncLogDateRangeAsync uses.
  if (!await ctx.waitForRecreationGateAsync()) return { ran: false, skipReason: 'recreation-gate' };
  if (!ctx.acquireReadLock()) return { ran: false, skipReason: 'rw-lock' };
  const logHeader = `Db:${ctx.internalDatabaseIdentifier}: [pull catch-up]`;
  try {
    if (!ctx.localDb || !ctx.remoteDb) return { ran: false, skipReason: 'recreated' }; // recreation may have nulled them while we waited

    // #3987 A6: a truncated response body surfaces as a JSON SyntaxError
    // ("Unterminated string in JSON at position ...", APP-46Q) and used to
    // abort the whole catch-up. Truncation is most plausibly a transient
    // connection cut mid-body, so retry the same read once. If the retry
    // throws again it propagates to the outer catch, which reports
    // PullCatchupFailed with truncated: true so recurrence is measurable
    // (that data would justify a pagination/range-halving follow-up).
    // #6230: but a server answering 5xx with a non-JSON body throws the SAME
    // SyntaxError, and the message cannot tell the two apart (WebKit says "The
    // string did not match the expected pattern." for both). So first ask the
    // server for its status with a raw GET of the database root: if it is down,
    // give up without the retry and without calling it a truncation.
    // Shared by the rev pass and the body fetch below.
    // pouchdb-browser ships no response typings, so pin the shape we consume.
    type RemoteAllDocsRow = { id: string; doc?: any; value?: { rev: string; deleted?: boolean } };
    // one remote read; a 5xx that PouchDB could parse is marked as a server outage HERE, at the
    // remote read, because a local-store error carries status 500 too (see the error class)
    const readRemoteAllDocsOnceAsync = async (opts: object): Promise<{ rows: RemoteAllDocsRow[] }> => {
      try {
        return await ctx.remoteDb.allDocs(opts);
      } catch (readErr: any) {
        if (isServerErrorStatus(readErr)) throw new PullCatchupServerUnavailableError(readErr.status, readErr);
        throw readErr;
      }
    };
    const readRemoteAllDocsWithRetryAsync = async (opts: object): Promise<{ rows: RemoteAllDocsRow[] }> => {
      try {
        return await readRemoteAllDocsOnceAsync(opts);
      } catch (readErr: any) {
        if (readErr?.name !== 'SyntaxError') throw readErr;
        const probe = await probeRemoteStatusAsync();
        if (probe === kProbeAnswerNotJson) throw new PullCatchupServerUnavailableError(null, readErr);
        if (typeof probe === 'number' && probe >= kFirstServerErrorStatus) {
          throw new PullCatchupServerUnavailableError(probe, readErr);
        }
        console.log(`${logHeader} remote read failed with SyntaxError (likely truncated response body) — retrying once:`, readErr?.message);
        return await readRemoteAllDocsOnceAsync(opts);
      }
    };
    // what the server says about the database root: its status; `kProbeAnswerNotJson` when the
    // probe itself got a non-JSON answer (a fresh remote handle reads the server's info document
    // as JSON before it sends anything, so an HTML outage page fails the probe the same way);
    // or null when the probe failed for any other reason, and nothing is known
    const probeRemoteStatusAsync = async (): Promise<number | typeof kProbeAnswerNotJson | null> => {
      try {
        const response = await ctx.remoteDb.fetch('', { method: 'GET' });
        return typeof response?.status === 'number' ? response.status : null;
      } catch (probeErr) {
        if ((probeErr as any)?.name === 'SyntaxError') return kProbeAnswerNotJson;
        // probe failure handled by the caller: it retries the read, and a read that fails again is reported with its own error
        console.log(`${logHeader} status probe after a SyntaxError failed — retrying the read anyway:`, (probeErr as any)?.message ?? probeErr);
        return null;
      }
    };

    // 1. REV-FIRST pass (S7, #4027 Step 25): fetch only ids + revs for the priority
    //    window (include_docs:false), mirroring the initial-sync selector: all non-log
    //    docs + recent/future logs. Bodies are fetched later, ONLY for the docs we
    //    actually missed — a healthy resume past the cooldown now downloads a few KB of
    //    revs instead of every body in the window. What counts as "missed" is unchanged
    //    #3862/#3987-A4 semantics: selectMissedDocs is generic over {_id,_rev} stubs.
    const remoteRanges = ctx.getPrioritySyncKeyRanges();
    const remoteStubs = new Map<string, { _id: string; _rev: string }>();
    for (const range of remoteRanges) {
      const result = await readRemoteAllDocsWithRetryAsync({ ...range, include_docs: false });
      for (const row of result.rows) {
        // an allDocs range row always carries id + value.rev; deleted docs are marked
        // in value.deleted (there is no body to inspect on a rev-only row)
        if (!row.value?.rev || row.value.deleted) continue;
        // replicationFilter inspects only _id (server-only design docs), so an id stub suffices
        if (!ctx.replicationFilter({ _id: row.id } as PouchDoc)) continue;
        remoteStubs.set(row.id, { _id: row.id, _rev: row.value.rev });
      }
    }
    if (remoteStubs.size === 0) {
      sessionStorage.lastPullCatchup_utc = DateTime.utc().toISO();
      return { ran: true, checkedCount: 0, missedCount: 0, bodyBytes: 0, durationMs: Math.round(performance.now() - runStartMs) };
    }

    // 2. compare to local: keep only docs we are missing, that are newer on the server,
    //    or whose same-generation rev hash diverged (#3987 A4, defect D6).
    const ids = [...remoteStubs.keys()];
    const localResult = await ctx.localDb.allDocs({ keys: ids, include_docs: false });
    const missedEntries = selectMissedDocs([...remoteStubs.values()], localResult.rows as PullCatchupLocalRow[]);

    // convergence guard (#3987 A4): grafting a same-gen divergent remote leaf does NOT
    // guarantee it becomes the local winner — if the local leaf keeps winning, every
    // subsequent catch-up would re-flag and re-graft the same doc forever, inflating the
    // sync_pull_catchup metric. So for the (few) sameGen entries, skip the doc when the
    // remote rev is already present locally as a leaf (winner or conflict branch).
    // Absent-locally and newer-generation entries keep the existing fast path.
    // The check needs only ids + revs, so it runs on the rev-pass stubs.
    const missedStubs: Array<{ _id: string; _rev: string }> = [];
    const sameGenDocIds = new Set<string>();
    for (const entry of missedEntries) {
      // if not a same-gen divergence, keep the existing fast path — no leaf check needed
      if (!entry.sameGen) {
        missedStubs.push(entry.doc);
        continue;
      }
      try {
        const localDoc = await ctx.localDb.get(entry.doc._id, { conflicts: true });
        const localLeafRevs = [localDoc._rev, ...((localDoc as any)._conflicts ?? [])];
        if (localLeafRevs.includes(entry.doc._rev)) continue; // already grafted earlier
      } catch (err) {
        // benign: if the leaf check itself fails we fall through and graft — the graft
        // is bulkDocs(new_edits:false), which is idempotent for an already-known rev
        console.log(`${logHeader} same-gen leaf check failed for ${entry.doc._id} — grafting anyway:`, (err as any)?.message ?? err);
      }
      sameGenDocIds.add(entry.doc._id);
      missedStubs.push(entry.doc);
    }

    // record the run time now — a clean "nothing missed" pass still counts for the cooldown
    sessionStorage.lastPullCatchup_utc = DateTime.utc().toISO();

    if (missedStubs.length === 0) {
      console.log(`${logHeader} up to date (${ids.length} docs checked, rev-only)`);
      return { ran: true, checkedCount: ids.length, missedCount: 0, bodyBytes: 0, durationMs: Math.round(performance.now() - runStartMs) };
    }

    // 2b. body fetch (S7, #4027 Step 25): now that we know WHICH docs were missed, fetch
    //     just those bodies in one keyed read. A doc deleted or filtered between the rev
    //     pass and this fetch simply drops out (nothing to graft — the restarted live
    //     sync delivers the deletion); a doc whose rev advanced in between grafts the
    //     NEWER body, which is still a doc this device had missed.
    const bodyResult = await readRemoteAllDocsWithRetryAsync({ keys: missedStubs.map(stub => stub._id), include_docs: true });
    const missedDocs: any[] = [];
    let bodyBytes = 0;
    for (const row of bodyResult.rows) {
      const doc = row.doc;
      if (!doc || doc._deleted) continue;
      if (!ctx.replicationFilter(doc)) continue; // skip server-only design docs
      missedDocs.push(doc);
      // approximate payload size for the resume_recovery event — missed docs are few,
      // so the stringify here is cheap
      bodyBytes += JSON.stringify(doc).length;
    }
    if (missedDocs.length === 0) {
      console.log(`${logHeader} all ${missedStubs.length} missed doc(s) vanished before the body fetch — nothing to graft`);
      return { ran: true, checkedCount: ids.length, missedCount: 0, bodyBytes: 0, durationMs: Math.round(performance.now() - runStartMs) };
    }

    // 3. graft the missing docs into local without creating new revisions, then refresh
    //    any open screens through the same identity-map path the real-time listener uses.
    console.warn(`${logHeader} live sync had missed ${missedDocs.length} doc(s) — grafting`);
    await ctx.localDb.bulkDocs(missedDocs, { new_edits: false });
    // #4249: collect the docs the loop below decides are real changes and hand
    // them to the pulled-doc listeners as ONE batch after the loop. This graft
    // can carry thousands of docs, so per-doc listener work here would land in
    // the same hot path #4243 froze cold starts in.
    // the delivery sits in a finally: if an apply throws part-way through, the
    // docs collected so far must still reach the listeners, or that whole
    // batch of cross-device changes is lost with nothing to re-trigger it
    const catchupListenerDocs: any[] = [];
    try {
      for (const doc of missedDocs) {
        // if this was a same-gen graft, the remote leaf may LOSE PouchDB's deterministic
        // winner selection to the local leaf — applying the remote body would push a losing
        // body/rev into open screens that a fresh db.get would contradict. re-read the actual
        // winner and apply that instead. absent-locally and newer-generation grafts always
        // win, so they keep the direct apply with no extra read.
        if (sameGenDocIds.has(doc._id)) {
          try {
            const winner = await ctx.localDb.get(doc._id);
            ctx.collectAppliedPulledDoc(winner, catchupListenerDocs);
          } catch (winnerErr) {
            // benign fallback: if the winner re-read fails, apply the remote body as before —
            // the transient cache mismatch self-heals on the next read or pull
            console.log(`${logHeader} winner re-read failed for ${doc._id} — applying remote body:`, (winnerErr as any)?.message ?? winnerErr);
            ctx.collectAppliedPulledDoc(doc, catchupListenerDocs);
          }
          continue;
        }
        ctx.collectAppliedPulledDoc(doc, catchupListenerDocs);
      }
    } finally {
      ctx.deliverPulledDocsToListeners(catchupListenerDocs);
    }

    // telemetry: this stall was previously silent. fire the Mixpanel volume metric
    // unconditionally so we can measure how often the live pull silently fell behind, and a
    // gated once-per-session Sentry signal only on a healthy foreground connection (a
    // transient/offline/background state is expected and self-heals — same gate as the
    // push-side checkpoint-drift report). doc-type derivation is shared with
    // sync_checkpoint_drift — see docTypeFromId for why it is not a last-dash split.
    const docTypes = [...new Set(missedDocs.map(d => docTypeFromId(d._id)))];
    // F13 (#3988): whether we are about to restart the live sync below (step 4).
    // hoisted here so the same value rides the telemetry event — production data
    // then shows how often older-than-priority-window misses are healed by the
    // checkpoint-based restart (the undocumented path this measures) vs the
    // grafted priority window alone. hoisting is safe: nothing between here and
    // the step-4 branch mutates syncHandler / syncHandlerIsInitial.
    const willRestartLiveSync = ctx.syncHandler != null && !ctx.syncHandlerIsInitial;
    ctx.telemetry?.trackEvent('sync_pull_catchup', {
      missed_doc_count: missedDocs.length,
      doc_types: docTypes.join(','),
      live_sync_restarted: willRestartLiveSync,
      // S7 (#4027 Step 25): sizing for the rev-first split — how many docs were
      // rev-checked and how many body bytes the keyed fetch actually pulled
      checked_count: ids.length,
      body_bytes: bodyBytes,
    });
    // the database term is left out: this signal has never consulted the database state
    const environmentHealthy = isSyncDiagnosticEnvironmentHealthy({
      connectionStateIsOnline: ctx.connectionStateIsOnline,
      documentHidden: document.hidden,
      recentNetworkSyncError: ctx.hasRecentNetworkSyncError(),
    });
    if (environmentHealthy && !sessionStorage.pullCatchupReported) {
      sessionStorage.pullCatchupReported = '1';
      ctx.logging.trackSilentInfo('PullCatchupRecoveredMissedDocs', {
        missedDocCount: missedDocs.length,
        docTypes: docTypes.join(','),
        connectionState: ctx.connectionState,
        documentHidden: document.hidden,
        // stringify so Sentry doesn't normalize the nested error to "[Object]" (same as every other caller)
        lastSyncError: ctx.stringifiedLastSyncError(),
      });
    }

    // 4. the live pull was demonstrably stuck — restart the live sync to rebuild a fresh
    //    download connection for the rest of the session. reuses the same cancel + restart
    //    path ensureSyncActive / the 'Load failed' + auth-error handlers use.
    if (willRestartLiveSync) {
      console.log(`${logHeader} restarting live sync to rebuild the download connection`);
      ctx.cancelSync();
      void ctx.startSync(false);
    }
    return { ran: true, checkedCount: ids.length, missedCount: missedDocs.length, bodyBytes, durationMs: Math.round(performance.now() - runStartMs) };
  } catch (err) {
    // a transient/network failure of the catch-up itself is expected — it self-heals on the
    // next resume/online; surface only a genuinely unexpected failure so a real bug in this
    // recovery path is not swallowed forever.
    if (ctx.databaseRecovery.isIntentionalDestruction()) {
      // A purge or a restore is destroying this database ON PURPOSE right now: it cancels sync and
      // nulls `remoteDb` before it writes, so a catch-up that was already in flight then dies on a
      // null/closed handle. That is our own doing, not a fault in this recovery path — the same rule
      // `interceptDatabaseError` follows. Reporting it would be exactly the bug this branch exists to
      // fix: an artifact of deliberate destruction, raised as a Silent problem, red-lighting E2E's
      // unexpected-browser-error gate. Leave a breadcrumb, report nothing.
      console.log(`${logHeader} abandoned — the database is being destroyed on purpose`);
    } else if (isTransientFetchError(err)) {
      console.log(`${logHeader} skipped (transient):`, (err as any)?.message ?? err);
    } else {
      // #6230: a failure some other part of the app already owns is logged, not reported —
      // reporting it here only buried a real fault in this path among events that were never
      // this path's. asked only here, because handing an error to the recovery can start one.
      const ownedElsewhere = handOffFailureOwnedElsewhere(ctx, err);
      if (ownedElsewhere !== null) {
        console.log(`${logHeader} failed, not reported (${ownedElsewhere}):`, (err as any)?.message ?? err);
      } else {
        ctx.logging.trackSilentProblem('PullCatchupFailed', {
          db: ctx.internalDatabaseIdentifier,
          name: (err as any)?.name,
          message: (err as any)?.message,
          // #3987 A6: a SyntaxError here means BOTH reads of a range came back as
          // invalid JSON (the in-loop retry already ran) while the server itself
          // answered fine (#6230) — flag it so recurring truncation is measurable
          truncated: (err as any)?.name === 'SyntaxError',
        });
      }
    }
    return { ran: false, skipReason: 'error', durationMs: Math.round(performance.now() - runStartMs) };
  } finally {
    ctx.releaseReadLock();
  }
}
